import {
  QButton,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Group } from '../../../../../../api/model/group';
import { Tag } from '../../../../../../api/model/tag';
import { DocumentOverviewContext } from '../../../Context';
import { shouldShowManageTagsButton } from '../../../RenderLogic';
import { TagsLookup } from '../TagsLookup';

export type ManageTagsButtonProps = {
  tags: Tag[];
  groups: Group[];
};
export const ManageTagsButton = ({ tags, groups }: ManageTagsButtonProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const currentUser = useCurrentUser();
  const { qualioDocument, refetchDocumentOverviewData } = useContext(
    DocumentOverviewContext,
  );
  const canManageTags = useMemo(
    () => shouldShowManageTagsButton(qualioDocument, currentUser),
    [currentUser, qualioDocument],
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const commonToastProps = {
    id: 'overview-manage-tags-toast',
    replace: true,
  };

  const onSuccessCallback = () => {
    showToast({
      'data-cy': 'manage-tags-button-notify-success-toast',
      ...commonToastProps,
      status: 'success',
      title: t('notifications.tagsUpdated'),
      description: t('notifications.successfullyAppliedTagChanges'),
    });
    refetchDocumentOverviewData();
  };

  const onErrorCallback = (error: any) => {
    const { message } = error as Error;
    const hasNoAccessToTag = message.includes('accessing');
    const preventOwnerFromAccessing = error.response.data.message.includes(
      'prevent the owner from accessing',
    );
    const preventOwnerFromAccessingToastMessage = t(
      'notifications.tagChangesRestrictOwnerAccess',
    );
    let description = t('notifications.failedToApplyTagChanges');
    if (hasNoAccessToTag) {
      description = message;
    } else if (preventOwnerFromAccessing) {
      description = preventOwnerFromAccessingToastMessage;
    }
    showToast({
      'data-cy': 'manage-tags-button-notify-error-toast',
      ...commonToastProps,
      status: 'error',
      title: t('notifications.error'),
      description,
    });
  };

  if (!canManageTags) {
    return null;
  }

  return (
    <>
      <QButton
        data-cy="document-manage-tags-button"
        variant="ghost"
        onClick={() => setIsModalOpen(true)}
      >
        {t('buttons.manage')}
      </QButton>
      <TagsLookup
        isOpen={isModalOpen}
        tags={tags}
        groups={groups}
        qualioDocument={qualioDocument}
        onClose={() => setIsModalOpen(false)}
        onSuccess={onSuccessCallback}
        onError={onErrorCallback}
      />
    </>
  );
};
